import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import {
  CommonWebsiteContentApi,
  CommonWebsiteSection,
  mapLandingModalToApi,
  mapSuccessModalToApi,
  hasLandingPageApiContent,
  hasSuccessPageApiContent
} from '../optional-setting/common-website-content.mapper';

export interface CampaignContentData {
  email?: {
    type: 'email';
    platform: 'email';
    email_subject: string;
    email_from_id: string;
    email_logo_img: string | File | null;
    email_body_img: string | File | null;
    email_header: string;
    email_body_text: string;
    email_body_text2: string;
    email_body_text3: string;
    email_body_text4: string;
    email_button_text: string;
    email_button_position: 'top' | 'bottom';
    email_button_color: string;
    email_footer_name: string;
    email_footer_street: string;
    email_footer_city: string;
    email_footer_state: string;
    email_footer_zip: string;
    email_reply_to: string;
    email_footer_phone: string;
    email_test_id: string;
  };
  facebook?: {
    platform: 'facebook';
    fb_logo_img: string | File | null;
    fb_img: string | File | null;
    fb_name: string;
    fb_caption: string;
    fb_headline: string;
    fb_button_text: string;
  };
  sms?: {
    platform: 'sms';
    sms_sender_contact: string;
    sms_body: string;
    sms_link: string;
  };
  mms?: {
    platform: 'mms';
    status: boolean;
    mms_config: {
      message_type: 'mms';
      mms_fallback: { text: string; behavior: string };
      bubble_1_coupon: {
        image_width: number;
        image_height: number;
        image_caption: string;
        image_alt_text: string;
        coupon_image_url: string;
      };
      bubble_2_text_body: string;
    };
  };
  mms_campaign?: {
    platform: 'mms_campaign';
    sms_sender_contact: string | null;
    mms_config: {
      message_type: 'mms';
      mms_fallback: { text: string; behavior: string };
      mms_link: string;
      bubble_1_coupon: {
        image_width: number;
        image_height: number;
        image_caption: string;
        image_alt_text: string;
        coupon_image_url: string;
      };
      bubble_2_text_body: string;
    };
  };
  rcs?: {
    platform: 'rcs';
    rcs_config: {
      message_type: 'rcs';
      mms_fallback: { text: string; behavior: string };
      bubble_1_coupon: {
        image_width: number;
        image_height: number;
        image_caption: string;
        image_alt_text: string;
        coupon_image_url: string;
      };
      bubble_2_text_body: string;
    };
  };
  /** Optional-settings SMS delivery (`platform: sms_delivery`). */
  sms_delivery?: {
    platform: 'sms_delivery';
    sms_config: {
      sms_body: string;
    };
  };
  /** Optional-settings email delivery (`platform: email_delivery`). */
  delivery_email?: {
    platform: 'email_delivery';
    status: boolean;
    email_config: {
      body: string;
      subject: string;
      preheader: string;
    };
  };
  google?: {
    platform: 'google';
    g_ad_img: string | File | null;
    g_ad_title: string;
    g_ad_company: string;
  };
  common_website?: CommonWebsiteContentApi;
}

/**
* Manages and stores campaign content for multiple platforms (email, Facebook, SMS, Google) using a BehaviorSubject; exposes campaignData$ as an observable for components and services to subscribe to and provides methods to update, clear, initialize, and reset platform-specific content.
* @component
* @selector N/A (Injectable service providedIn: 'root')
* @example
*   // Inject and use in an Angular component or another service
*   constructor(private campaignDataManager: CampaignDataManagerService) {}
*   this.campaignDataManager.updatePlatformData('email', { subject: 'Promo', fromEmail: 'noreply@...' });
* @input {any} campaignData - Initial campaign payload passed to initializeFromCampaignData(campaignData) to seed the internal store (supports array or object formats).
* @output {Observable<CampaignContentData>} campaignData$ - Observable stream that emits the current CampaignContentData whenever it changes.
*
* Key methods:
* - getCampaignData(): CampaignContentData — returns the current snapshot of the campaign content stored in the service.
* - updatePlatformData(platform: keyof CampaignContentData, data: any): void — updates or creates content for a specific platform (email, facebook, sms, google), normalizing provided fields and emitting the updated state.
* - clearPlatformData(platform: keyof CampaignContentData): void — clears a specific platform's content back to default/empty values and emits the change.
* - getAllContents(): any[] — returns an array of all platform contents (useful for preparing API payloads and updates).
* - initializeFromCampaignData(campaignData: any): void — initializes the internal store from existing campaign data (handles both array-of-contents and object formats).
* - resetAllData(): void — clears all campaign content and emits an empty state.
*
* Lifecycle / notes:
* - Provided in root as a singleton service; there are no Angular lifecycle hooks on this service. Consumers should subscribe to campaignData$ to react to updates and call initialize/reset methods when appropriate.
*/
@Injectable({
  providedIn: 'root'
})
export class CampaignDataManagerService {
  private campaignDataSubject = new BehaviorSubject<CampaignContentData>({});
  public campaignData$ = this.campaignDataSubject.asObservable();

  constructor() {}

  // Get current campaign data
  getCampaignData(): CampaignContentData {
    return this.campaignDataSubject.value;
  }

  // Update specific platform data
  /**
  * Update platform-specific campaign data in the internal store and emit the updated data.
  * @example
  * pdatePlatformData('email', { subject: 'Sale', fromEmail: 'noreply@example.com' })
  * undefined
  * @param {{keyof CampaignContentData}} {{platform}} - The platform key to update (e.g., 'email', 'facebook', 'sms', 'google').
  * @param {{any}} {{data}} - Payload containing platform-specific fields used to populate and override campaign content.
  * @returns {{void}} Update internal campaign data and notify subscribers via campaignDataSubject.
  **/
  updatePlatformData(platform: keyof CampaignContentData, data: any): void {
    const currentData = this.getCampaignData();
    const updatedData = { ...currentData };
    
    if (platform === 'email') {
      updatedData.email = {
        type: 'email',
        platform: 'email',
        email_subject: data.subject || '',
        email_from_id: data.fromEmail || '',
        email_logo_img: data.logoUrl || data.logo || null,
        email_body_img: data.imageUrl || data.image || null,
        email_header: data.header || '',
        email_body_text: data.bodyText || '',
        email_body_text2: data.body2Text || '',
        email_body_text3: data.body3Text || '',
        email_body_text4: data.body4Text || '',
        email_button_text: data.buttonText || '',
        email_button_position: data.buttonPosition || 'bottom',
        email_button_color: data.buttonColor || '#326FD1',
        email_footer_name: data.carWashName || '',
        email_footer_street: data.street || '',
        email_footer_city: data.city || '',
        email_footer_state: data.state || '',
        email_footer_zip: data.zipCode || '',
        email_reply_to: data.email || '',
        email_footer_phone: data.phoneNumber || '',
        email_test_id: 'test@company.com'
      };
    } else if (platform === 'facebook') {
      updatedData.facebook = {
        platform: 'facebook',
        fb_logo_img: data.logoUrl || data.logo || null,
        fb_img: data.imageUrl || data.image || null,
        fb_name: data.name || '',
        fb_caption: data.caption || '',
        fb_headline: data.headline || '',
        fb_button_text: data.buttonText || ''
      };
    } else if (platform === 'sms') {
      // Only one mobile campaign channel allowed — remove mms_campaign when SMS is set
      delete updatedData.mms_campaign;
      updatedData.sms = {
        platform: 'sms',
        sms_sender_contact: data.sms_sender_contact || data.senderId || '',
        sms_body: data.sms_body || data.textMessage || '',
        sms_link: data.sms_link || data.linkText || ''
      };
    } else if (platform === 'mms') {
      updatedData.mms = {
        platform: 'mms',
        status: data.status ?? true,
        mms_config: data.mms_config
      };
    } else if (platform === 'mms_campaign') {
      // Only one mobile campaign channel allowed — remove sms when mms_campaign is set
      delete updatedData.sms;
      updatedData.mms_campaign = {
        platform: 'mms_campaign',
        sms_sender_contact: data.sms_sender_contact ?? null,
        mms_config: data.mms_config
      };
    } else if (platform === 'rcs') {
      updatedData.rcs = {
        platform: 'rcs',
        rcs_config: data.rcs_config
      };
    } else if (platform === 'sms_delivery') {
      updatedData.sms_delivery = {
        platform: 'sms_delivery',
        sms_config: data.sms_config ?? { sms_body: data.messageBody?.trim() || '' }
      };
    } else if (platform === 'delivery_email') {
      updatedData.delivery_email = {
        platform: 'email_delivery',
        status: data.status ?? true,
        email_config: data.email_config
      };
    } else if (platform === 'google') {
      updatedData.google = {
        platform: 'google',
        g_ad_img: data.imageUrl || data.image || null,
        g_ad_title: data.title || '',
        g_ad_company: data.company || ''
      };
    } else if (platform === 'common_website') {
      const existing = currentData.common_website || { platform: 'common_website' as const };
      const section: CommonWebsiteSection = data.section || 'landing_page';

      if (section === 'landing_page') {
        updatedData.common_website = {
          platform: 'common_website',
          landing_page: mapLandingModalToApi(data, data.logo),
          success_page: existing.success_page
        };
      } else {
        updatedData.common_website = {
          platform: 'common_website',
          landing_page: existing.landing_page,
          success_page: mapSuccessModalToApi(data, data.logoUrl)
        };
      }
    }

    this.campaignDataSubject.next(updatedData);
  }

  // Clear specific platform data
  /**
  * Clears and resets the campaign content for a specific platform to its default empty values and emits the updated campaign data.
  * @example
  * learPlatformData('email')
  * void
  * @param {keyof CampaignContentData} platform - Platform key to clear and reset (e.g., 'email', 'facebook', 'sms', 'google').
  * @returns {void} Does not return a value; updates are emitted via the campaign data subject.
  **/
  clearPlatformData(platform: keyof CampaignContentData, section?: CommonWebsiteSection): void {
    const currentData = this.getCampaignData();
    const updatedData = { ...currentData };
    
    if (platform === 'email') {
      updatedData.email = {
        type: 'email',
        platform: 'email',
        email_subject: '',
        email_from_id: '',
        email_logo_img: null,
        email_body_img: null,
        email_header: '',
        email_body_text: '',
        email_body_text2: '',
        email_body_text3: '',
        email_body_text4: '',
        email_button_text: '',
        email_button_position: 'bottom',
        email_button_color: '#326FD1',
        email_footer_name: '',
        email_footer_street: '',
        email_footer_city: '',
        email_footer_state: '',
        email_footer_zip: '',
        email_reply_to: '',
        email_footer_phone: '',
        email_test_id: 'test@company.com'
      };
    } else if (platform === 'facebook') {
      updatedData.facebook = {
        platform: 'facebook',
        fb_logo_img: null,
        fb_img: null,
        fb_name: '',
        fb_caption: '',
        fb_headline: '',
        fb_button_text: ''
      };
    } else if (platform === 'sms') {
      updatedData.sms = {
        platform: 'sms',
        sms_sender_contact: '',
        sms_body: '',
        sms_link: ''
      };
    } else if (platform === 'mms') {
      delete updatedData.mms;
    } else if (platform === 'mms_campaign') {
      // mms_config has a strict backend schema (behavior-keyed union); there's no blank
      // shape that satisfies it, so clearing removes the platform entirely instead.
      delete updatedData.mms_campaign;
    } else if (platform === 'rcs') {
      delete updatedData.rcs;
    } else if (platform === 'sms_delivery') {
      delete updatedData.sms_delivery;
    } else if (platform === 'delivery_email') {
      delete updatedData.delivery_email;
    } else if (platform === 'google') {
      updatedData.google = {
        platform: 'google',
        g_ad_img: null,
        g_ad_title: '',
        g_ad_company: ''
      };
    } else if (platform === 'common_website') {
      const existing = currentData.common_website;
      const targetSection = section || 'landing_page';

      if (!existing) {
        return;
      }

      if (targetSection === 'landing_page') {
        if (hasSuccessPageApiContent(existing.success_page)) {
          updatedData.common_website = {
            platform: 'common_website',
            success_page: existing.success_page
          };
        } else {
          delete updatedData.common_website;
        }
      } else if (hasLandingPageApiContent(existing.landing_page)) {
        updatedData.common_website = {
          platform: 'common_website',
          landing_page: existing.landing_page
        };
      } else {
        delete updatedData.common_website;
      }
    }

    this.campaignDataSubject.next(updatedData);
  }

  // Get all contents for API update
  /**
   * Collects and returns the campaign content objects for each supported channel in a fixed order.
   * @example
   * etAllContents()
   * [ { channel: 'email', ... }, { channel: 'facebook', ... }, { channel: 'sms', ... }, { channel: 'google', ... } ]
   * @returns {any[]} Array of content objects for the campaign channels (email, facebook, sms, google) in that order.
   */
  getAllContents(): any[] {
    const data = this.getCampaignData();
    const contents: any[] = [];

    // Always include all platforms, even if empty
    if (data.email) {
      contents.push(data.email);
    }
    if (data.facebook) {
      contents.push(data.facebook);
    }
    if (data.sms) {
      contents.push(data.sms);
    }
    if (data.mms) {
      contents.push(data.mms);
    }
    if (data.mms_campaign) {
      contents.push(data.mms_campaign);
    }
    if (data.rcs) {
      contents.push(data.rcs);
    }
    if (data.sms_delivery) {
      contents.push(data.sms_delivery);
    }
    if (data.delivery_email) {
      contents.push(data.delivery_email);
    }
    if (data.google) {
      contents.push(data.google);
    }
    if (
      data.common_website &&
      (hasLandingPageApiContent(data.common_website.landing_page) ||
        hasSuccessPageApiContent(data.common_website.success_page))
    ) {
      contents.push(data.common_website);
    }

    return contents;
  }

  // Initialize from existing campaign data
  /**
  * Initialize internal campaign content state from raw campaign data and emit the normalized content via campaignDataSubject.
  * @example
  * nitializeFromCampaignData({ contents: [{ platform: 'email', ... }, { platform: 'facebook', ... }] })
  * void
  * @param {{any}} {{campaignData}} - Campaign data object which may contain a 'contents' property in either array or object format.
  * @returns {{void}} Nothing is returned; the function updates and emits normalized campaign content via campaignDataSubject.
  **/
  initializeFromCampaignData(campaignData: any): void {
    const contentData: CampaignContentData = {};

    if (campaignData && campaignData.contents) {
      // Handle array format
      if (Array.isArray(campaignData.contents)) {
        campaignData.contents.forEach((content: any) => {
          if (content.platform === 'email_delivery') {
            contentData.delivery_email = content;
          } else if (
            content.platform === 'email' &&
            content.email_config &&
            typeof content.email_config === 'object'
          ) {
            contentData.delivery_email = content;
          } else if (content.platform === 'email' || content.type === 'email') {
            contentData.email = content;
          } else if (content.platform === 'facebook') {
            contentData.facebook = content;
          } else if (content.platform === 'sms_delivery') {
            contentData.sms_delivery = content;
          } else if (
            content.platform === 'sms' &&
            content.sms_config &&
            typeof content.sms_config === 'object'
          ) {
            contentData.sms_delivery = content;
          } else if (content.platform === 'sms') {
            contentData.sms = content;
          } else if (content.platform === 'mms') {
            contentData.mms = content;
          } else if (content.platform === 'mms_campaign') {
            contentData.mms_campaign = content;
          } else if (content.platform === 'rcs') {
            contentData.rcs = content;
          } else if (content.platform === 'google') {
            contentData.google = content;
          } else if (content.platform === 'common_website') {
            contentData.common_website = content;
          }
        });
      }
      // Handle object format
      else if (campaignData.contents.email) {
        contentData.email = campaignData.contents.email;
      }
      if (campaignData.contents.facebook) {
        contentData.facebook = campaignData.contents.facebook;
      }
      if (campaignData.contents.sms_delivery) {
        contentData.sms_delivery = campaignData.contents.sms_delivery;
      }
      if (campaignData.contents.sms) {
        contentData.sms = campaignData.contents.sms;
      }
      if (campaignData.contents.delivery_email) {
        contentData.delivery_email = campaignData.contents.delivery_email;
      }
      if (campaignData.contents.email_delivery) {
        contentData.delivery_email = campaignData.contents.email_delivery;
      }
      if (campaignData.contents.mms) {
        contentData.mms = campaignData.contents.mms;
      }
      if (campaignData.contents.mms_campaign) {
        contentData.mms_campaign = campaignData.contents.mms_campaign;
      }
      if (campaignData.contents.rcs) {
        contentData.rcs = campaignData.contents.rcs;
      }
      if (campaignData.contents.google) {
        contentData.google = campaignData.contents.google;
      }
      if (campaignData.contents.common_website) {
        contentData.common_website = campaignData.contents.common_website;
      }
    }

    this.campaignDataSubject.next(contentData);
  }

  // Reset all data
  resetAllData(): void {
    this.campaignDataSubject.next({});
  }
}
